import React, {useContext, useEffect, useRef, useState} from "react";
import {css, CSSObject, forBrands, getFont} from "@core-ui/react-stitch";
import {Transition, TransitionStatus, transitions} from "@core-ui/motion";
import {AccordionContext} from "./AccordionContext";
import {PanelContext} from "./PanelContext";
import {AccordionStyleFn, DetailsProps} from "./types";

const getSpacing: AccordionStyleFn = ({theme, crossBrand}) => {
  return forBrands(
    theme,
    {
      default: () => ({
        paddingBottom: "1rem",
      }),
      br: () => ({
        paddingBottom: "1.5rem",
      }),
    },
    crossBrand
  ) as string;
};

export const transitionTimeout = {
  enter:
    parseFloat(transitions.expressive.in.transitionDuration) +
    parseFloat(transitions.userTriggeredDelay),
  exit:
    parseFloat(transitions.performance.out.transitionDuration) +
    parseFloat(transitions.userTriggeredDelay),
};

export function PanelDetails({children, className}: DetailsProps): JSX.Element {
  const detailsRef = useRef<HTMLDivElement>(null);
  const {crossBrand} = useContext(AccordionContext);
  const {expanded} = useContext(PanelContext);
  const [detailsHeight, setDetailsHeight] = useState(0);

  const transitionCSS = expanded
    ? `${transitions.expressive.in.transitionDuration} ${transitions.expressive.in.transitionTimingFunction} ${transitions.userTriggeredDelay}`
    : `${transitions.performance.out.transitionDuration} ${transitions.performance.out.transitionTimingFunction} ${transitions.userTriggeredDelay}`;

  useEffect(() => {
    if (detailsRef.current) {
      setDetailsHeight(detailsRef.current.offsetHeight);
    }
  }, [children, expanded]);

  const transitionStyles: Record<TransitionStatus, CSSObject> = {
    entering: {height: detailsHeight, opacity: 1},
    entered: {height: detailsHeight, opacity: 1},
    exiting: {height: 0, opacity: 0},
    exited: {height: 0, opacity: 0},
    unmounted: {},
  };

  const detailsWrapperCSS: AccordionStyleFn<{
    transitionState: TransitionStatus;
  }> = ({transitionState}) => css`
    height: 0;
    opacity: 0;
    transition: height ${transitionCSS}, opacity ${transitionCSS};
    ${transitionStyles[transitionState]};
  `;

  const detailsCSS: AccordionStyleFn = (props) =>
    css`
      ${getFont("primary")(props)}
      display: block;
      height: auto;
      ${getSpacing({...props})}
    `;

  return (
    <Transition in={expanded} timeout={transitionTimeout}>
      {(transitionState) => (
        <div
          aria-hidden={!expanded}
          css={(theme) => detailsWrapperCSS({theme, transitionState})}
        >
          <div
            ref={detailsRef}
            className={className}
            css={(theme) => detailsCSS({theme, crossBrand})}
          >
            {children}
          </div>
        </div>
      )}
    </Transition>
  );
}
